import React, { useState, useEffect, useCallback } from 'react';
import { Invoice } from '../types';
import { apiService } from '../services/api';

interface EmailHistory {
  email_id: string;
  invoice_id: string;
  recipient_email: string;
  subject: string;
  sent_at: string;
  status: string;
  error_message?: string;
  template_name?: string;
  language?: string;
  has_attachments?: boolean;
  attachment_names?: string;
}

interface InvoiceDetailsEmailHistoryTabProps {
  invoice: Invoice;
}

const InvoiceDetailsEmailHistoryTab: React.FC<InvoiceDetailsEmailHistoryTabProps> = ({ 
  invoice 
}) => {
  const [emailHistory, setEmailHistory] = useState<EmailHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  
  const fetchEmailHistory = useCallback(async () => {
    try {
      console.log(`[InvoiceDetailsEmailHistoryTab] Fetching email history for invoice ${invoice.invoice_id}`);
      setLoading(true);
      setError(null);
      
      const response = await apiService.axiosInstance.get(`/api/v1/invoices/${invoice.invoice_id}/emails`);
      console.log(`[InvoiceDetailsEmailHistoryTab] Email history response:`, response.data);
      
      setEmailHistory(response.data || []);
      console.log(`[InvoiceDetailsEmailHistoryTab] Successfully loaded ${response.data?.length || 0} email records`);
    } catch (err: any) {
      console.error(`[InvoiceDetailsEmailHistoryTab] Error fetching email history:`, {
        error: err,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        invoiceId: invoice.invoice_id
      });
      setError(err.response?.data?.detail || err.message || 'Failed to load email history');
    } finally {
      setLoading(false);
    }
  }, [invoice.invoice_id]);

  useEffect(() => {
    console.log(`[InvoiceDetailsEmailHistoryTab] Component mounted for invoice ${invoice.invoice_id}`);
    fetchEmailHistory();
  }, [invoice.invoice_id, fetchEmailHistory]);

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      sent: 'badge-success',
      failed: 'badge-danger',
      pending: 'badge-warning'
    };
    return classes[status.toLowerCase()] || 'badge-secondary';
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handleResendEmail = async (emailId: string) => {
    try {
      console.log(`[InvoiceDetailsEmailHistoryTab] Resending email ${emailId}`);
      
      const response = await apiService.axiosInstance.post(`/api/v1/emails/${emailId}/resend`);
      console.log(`[InvoiceDetailsEmailHistoryTab] Resend response:`, response.data);
      
      // Refresh email history after resending
      await fetchEmailHistory();
      
      alert('Email resent successfully!');
    } catch (err: any) {
      console.error(`[InvoiceDetailsEmailHistoryTab] Error resending email:`, err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to resend email';
      alert(`Error: ${errorMessage}`);
    }
  };

  if (loading) {
    return (
      <div className="email-history-tab">
        <div className="loading">
          <div className="spinner" />
          <p>Loading email history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-history-tab">
        <div className="card">
          <div className="card-header">
            <h3>Email History</h3>
          </div>
          <div className="card-body">
            <div className="alert alert-danger">
              <strong>⚠️ Error Loading Email History</strong>
              <p>{error}</p>
              <button onClick={fetchEmailHistory} className="btn btn-sm btn-primary">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="email-history-tab">
      <div className="card">
        <div className="card-header">
          <div className="header-content">
            <h3>Email History</h3>
            <button 
              onClick={fetchEmailHistory} 
              className="btn btn-sm btn-secondary"
              title="Refresh email history"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
        <div className="card-body">
          {emailHistory.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Recipient</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Language</th>
                    <th>Template</th>
                    <th>Attachments</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emailHistory.map((email) => (
                    <tr key={email.email_id}>
                      <td className="date-cell">
                        {formatDate(email.sent_at)}
                      </td>
                      <td className="recipient-cell">
                        <div className="recipient-info">
                          <span className="email-address">{email.recipient_email}</span>
                        </div>
                      </td>
                      <td className="subject-cell">
                        <div className="subject-content">
                          {email.subject}
                        </div>
                      </td>
                      <td className="status-cell">
                        <span className={`badge ${getStatusBadgeClass(email.status)}`}>
                          {email.status}
                        </span>
                        {email.error_message && (
                          <div className="error-details" title={email.error_message}>
                            <small className="error-text">
                              {email.error_message.length > 30 
                                ? `${email.error_message.substring(0, 30)}...` 
                                : email.error_message
                              }
                            </small>
                          </div>
                        )}
                      </td>
                      <td className="language-cell">
                        {email.language ? (
                          <span className="language-badge">
                            {email.language.toUpperCase()}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="template-cell">
                        {email.template_name ? (
                          <span className="template-name" title={email.template_name}>
                            {email.template_name.length > 20 
                              ? `${email.template_name.substring(0, 20)}...` 
                              : email.template_name
                            }
                          </span>
                        ) : (
                          <span className="text-muted">Custom</span>
                        )}
                      </td>
                      <td className="attachments-cell">
                        {email.has_attachments ? (
                          <div className="attachment-info">
                            <span className="attachment-icon" title={email.attachment_names || 'Has attachments'}>
                              📎
                            </span>
                            {email.attachment_names && (
                              <small className="attachment-names">
                                {email.attachment_names.length > 15 
                                  ? `${email.attachment_names.substring(0, 15)}...` 
                                  : email.attachment_names
                                }
                              </small>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button
                            onClick={() => handleResendEmail(email.email_id)}
                            className="btn btn-sm btn-outline-primary"
                            title="Resend this email"
                          >
                            📤 Resend
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📧</div>
              <h4>No Email History</h4>
              <p>No emails have been sent for this invoice yet.</p>
              <p className="text-muted">
                Use the "Send Emails" tab to send your first invoice email.
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .email-history-tab .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .email-history-tab .header-content h3 {
          margin: 0;
        }

        .email-history-tab .table-container {
          overflow-x: auto;
        }

        .email-history-tab .table {
          width: 100%;
          border-collapse: collapse;
          margin: 0;
        }

        .email-history-tab .table th {
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
          padding: 12px 8px;
          text-align: left;
          font-weight: 600;
          color: #495057;
          font-size: 14px;
          white-space: nowrap;
        }

        .email-history-tab .table td {
          border: 1px solid #dee2e6;
          padding: 12px 8px;
          vertical-align: top;
          font-size: 14px;
        }

        .email-history-tab .table tbody tr:hover {
          background-color: #f8f9fa;
        }

        .email-history-tab .date-cell {
          min-width: 140px;
          font-family: monospace;
          font-size: 12px;
        }

        .email-history-tab .recipient-cell {
          min-width: 200px;
        }

        .email-history-tab .recipient-info .email-address {
          font-weight: 500;
          color: #007bff;
        }

        .email-history-tab .subject-cell {
          min-width: 250px;
          max-width: 300px;
        }

        .email-history-tab .subject-content {
          word-break: break-word;
          line-height: 1.4;
        }

        .email-history-tab .status-cell {
          min-width: 100px;
        }

        .email-history-tab .badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }

        .email-history-tab .badge-success {
          background-color: #28a745;
          color: white;
        }

        .email-history-tab .badge-danger {
          background-color: #dc3545;
          color: white;
        }

        .email-history-tab .badge-warning {
          background-color: #ffc107;
          color: #212529;
        }

        .email-history-tab .badge-secondary {
          background-color: #6c757d;
          color: white;
        }

        .email-history-tab .error-details {
          margin-top: 4px;
        }

        .email-history-tab .error-text {
          color: #dc3545;
          font-style: italic;
          font-size: 11px;
          display: block;
        }

        .email-history-tab .language-cell {
          min-width: 80px;
          text-align: center;
        }

        .email-history-tab .language-badge {
          background-color: #e9ecef;
          color: #495057;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 11px;
          font-weight: 600;
        }

        .email-history-tab .template-cell {
          min-width: 120px;
        }

        .email-history-tab .template-name {
          color: #6c757d;
          font-size: 12px;
        }

        .email-history-tab .attachments-cell {
          min-width: 100px;
          text-align: center;
        }

        .email-history-tab .attachment-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        }

        .email-history-tab .attachment-icon {
          font-size: 16px;
        }

        .email-history-tab .attachment-names {
          color: #6c757d;
          font-size: 10px;
        }

        .email-history-tab .actions-cell {
          min-width: 100px;
        }

        .email-history-tab .action-buttons {
          display: flex;
          gap: 5px;
        }

        .email-history-tab .btn-sm {
          padding: 4px 8px;
          font-size: 12px;
          border-radius: 3px;
        }

        .email-history-tab .btn-outline-primary {
          color: #007bff;
          border-color: #007bff;
          background-color: transparent;
        }

        .email-history-tab .btn-outline-primary:hover {
          color: white;
          background-color: #007bff;
          border-color: #007bff;
        }

        .email-history-tab .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #6c757d;
        }

        .email-history-tab .empty-icon {
          font-size: 48px;
          margin-bottom: 20px;
          opacity: 0.5;
        }

        .email-history-tab .empty-state h4 {
          margin-bottom: 10px;
          color: #495057;
        }

        .email-history-tab .empty-state p {
          margin-bottom: 8px;
          line-height: 1.5;
        }

        .email-history-tab .loading {
          text-align: center;
          padding: 40px 20px;
        }

        .email-history-tab .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .email-history-tab .alert {
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .email-history-tab .alert-danger {
          background-color: #f8d7da;
          border-color: #f5c6cb;
          color: #721c24;
        }

        .email-history-tab .text-muted {
          color: #6c757d;
        }

        @media (max-width: 768px) {
          .email-history-tab .table-container {
            font-size: 12px;
          }

          .email-history-tab .table th,
          .email-history-tab .table td {
            padding: 8px 4px;
          }

          .email-history-tab .subject-cell {
            max-width: 150px;
          }

          .email-history-tab .recipient-cell {
            min-width: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceDetailsEmailHistoryTab;